import { useState, useEffect } from 'react';
import { getCustomers, createCustomer, createLoginUser, apiFetch, getPendingCustomers, approveCustomer, rejectCustomer, type Customer, type PendingCustomer } from '../api';
import { useAuth } from '../context/AuthContext';

interface LoginUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface EditState {
  id: number;
  name: string;
  email: string;
  password: string;
}

export default function HubPage() {
  const { user, login: authLogin, logout } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selected, setSelected]   = useState<Customer | null>(null);
  const [users, setUsers]         = useState<LoginUser[]>([]);
  const [loading, setLoading]     = useState(true);

  // Opret kunde
  const [newCompany, setNewCompany] = useState('');
  const [newPlan, setNewPlan]       = useState('free');
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // Opret bruger
  const [newUserName, setNewUserName]   = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPass, setNewUserPass]   = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [userError, setUserError]       = useState('');

  // Skift plan
  const [savingPlan, setSavingPlan] = useState(false);

  // Rediger bruger
  const [editing, setEditing]     = useState<EditState | null>(null);
  const [saving, setSaving]       = useState(false);
  const [editError, setEditError] = useState('');

  // Pending customers (signups der afventer godkendelse)
  const [pending, setPending] = useState<PendingCustomer[]>([]);
  const [actioning, setActioning] = useState<number | null>(null);
  const [pendingPlan, setPendingPlan] = useState<Record<number, string>>({});

  // AI indstillinger
  const [aiCaps, setAiCaps]         = useState({ ai_daily_budget_usd: '5.00', ai_hourly_cap_per_customer: '50' });
  const [savingAi, setSavingAi]     = useState(false);
  const [aiSaved, setAiSaved]       = useState(false);

  // AI forbrug
  type AiStats = {
    daily: { day: string; cost_usd: number; calls: number; tokens: number }[];
    by_customer: { customer_id: number; company_name: string; cost_usd: number; calls: number }[];
    today: { cost_usd: number; budget_usd: number };
  };
  type AnthropicData = { usage: any; cost: any } | null;
  const [usageTab, setUsageTab]           = useState<'own' | 'anthropic'>('own');
  const [aiStats, setAiStats]             = useState<AiStats | null>(null);
  const [anthropicData, setAnthropicData] = useState<AnthropicData>(null);
  const [anthropicError, setAnthropicError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats]   = useState(false);

  useEffect(() => {
    apiFetch('/api/hub/system-config')
      .then(r => r.json())
      .then(cfg => setAiCaps(c => ({ ...c, ...cfg })))
      .catch(() => null);
    // Load egne stats ved åbning
    apiFetch('/api/hub/ai-stats').then(r => r.json()).then(setAiStats).catch(() => null);
  }, []);

  async function loadAnthropicData() {
    setLoadingStats(true);
    setAnthropicError(null);
    try {
      const res = await apiFetch('/api/hub/anthropic-usage');
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? 'Fejl');
      }
      setAnthropicData(await res.json());
    } catch (err: any) {
      setAnthropicError(err.message);
    } finally {
      setLoadingStats(false);
    }
  }

  useEffect(() => {
    getCustomers().then(setCustomers).finally(() => setLoading(false));
    loadPending();
  }, []);

  function loadPending() {
    getPendingCustomers().then(setPending).catch(() => null);
  }

  async function handleApprove(id: number) {
    setActioning(id);
    try {
      await approveCustomer(id, pendingPlan[id]);
      loadPending();
      getCustomers().then(setCustomers);
    } finally {
      setActioning(null);
    }
  }

  async function handleReject(id: number) {
    if (!confirm('Afvis denne kunde? Brugeren kan ikke logge ind igen efter afvisning.')) return;
    setActioning(id);
    try {
      await rejectCustomer(id);
      loadPending();
      getCustomers().then(setCustomers);
    } finally {
      setActioning(null);
    }
  }

  useEffect(() => {
    if (!selected) { setUsers([]); return; }
    reloadUsers(selected.id);
  }, [selected]);

  async function reloadUsers(customerId: number) {
    const res = await apiFetch(`/api/hub/customers/${customerId}/users`);
    setUsers(await res.json());
  }

  async function handleCreateCustomer() {
    if (!newCompany.trim()) return;
    setCreatingCustomer(true);
    try {
      await createCustomer({ company_name: newCompany.trim(), plan: newPlan });
      setCustomers(await getCustomers());
      setNewCompany('');
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function handleCreateUser() {
    if (!selected || !newUserName || !newUserEmail || !newUserPass) return;
    setUserError('');
    setCreatingUser(true);
    try {
      await createLoginUser({ customer_id: selected.id, name: newUserName, email: newUserEmail, password: newUserPass });
      await reloadUsers(selected.id);
      setNewUserName(''); setNewUserEmail(''); setNewUserPass('');
    } catch (err: unknown) {
      setUserError(err instanceof Error ? err.message : 'Fejl');
    } finally {
      setCreatingUser(false);
    }
  }

  async function handleSaveEdit() {
    if (!editing || !selected) return;
    setEditError('');
    setSaving(true);
    try {
      const res = await apiFetch(`/api/hub/users/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editing.name,
          email: editing.email,
          ...(editing.password ? { password: editing.password } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? 'Fejl');
      }
      await reloadUsers(selected.id);
      setEditing(null);
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Fejl');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePlan(plan: string) {
    if (!selected) return;
    setSavingPlan(true);
    await apiFetch(`/api/hub/customers/${selected.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    });
    const updated = await getCustomers();
    setCustomers(updated);
    setSelected(updated.find(c => c.id === selected.id) ?? null);
    setSavingPlan(false);
  }

  async function handleSaveAiCaps() {
    setSavingAi(true);
    setAiSaved(false);
    await apiFetch('/api/hub/system-config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(aiCaps),
    });
    setSavingAi(false);
    setAiSaved(true);
    setTimeout(() => setAiSaved(false), 2000);
  }

  async function handleImpersonate(customerId: number) {
    const res = await apiFetch(`/api/hub/impersonate/${customerId}`, { method: 'POST' });
    const data = await res.json();
    authLogin(data.token, data.user);
    window.location.href = '/';
  }

  const planLabel: Record<string, string> = { free: 'Free', plus: 'Plus', pro: 'Pro', trial: 'Trial' };
  const planColor: Record<string, string> = { free: 'bg-gray-100 text-gray-600', plus: 'bg-[var(--color-primary-soft-bg)] text-[var(--color-primary-hover)]', pro: 'bg-purple-100 text-purple-700', trial: 'bg-[var(--color-warning-100)] text-[var(--color-warning)]' };

  if (loading) return <div className="p-8 text-gray-400">Indlæser…</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-bold tracking-tight" style={{ letterSpacing: '-0.025em' }}>
              <span style={{ color: '#131916' }}>RentalFleet</span>
              <span className="text-sage-700">OS</span>
              <span className="text-ink-500 font-semibold ml-2">Hub</span>
            </h1>
            <p className="text-sm text-gray-500">Superadmin — {user?.name}</p>
          </div>
          <div className="flex gap-3">
            <a href="/hub/backup" className="text-sm text-[var(--color-primary)] hover:underline">Backup →</a>
            <a href="/" className="text-sm text-[var(--color-primary)] hover:underline">← Tilbage til app</a>
            <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-800">Log ud</button>
          </div>
        </div>

        {/* Pending kunder — vises kun hvis der er nogen */}
        {pending.length > 0 && (
          <div className="mb-8 bg-[var(--color-warning-100)] border border-amber-200 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-warning-100)]0" />
              Afventer godkendelse ({pending.length})
            </h2>
            <div className="space-y-3">
              {pending.map(p => (
                <div key={p.id} className="bg-white rounded-lg border border-amber-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{p.company_name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {p.city} · valgt plan: <span className="font-medium">{p.plan}</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-2">
                        {p.owner_name} · <span className="font-mono text-xs">{p.owner_email}</span>
                        {p.owner_verified ? (
                          <span className="ml-2 text-xs text-[var(--color-success)]">✓ verificeret</span>
                        ) : (
                          <span className="ml-2 text-xs text-[var(--color-warning)]">⚠ email ikke bekræftet</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Oprettet: {p.created_at}</div>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <select
                        value={pendingPlan[p.id] ?? p.plan}
                        onChange={e => setPendingPlan(prev => ({ ...prev, [p.id]: e.target.value }))}
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                        disabled={actioning === p.id}
                      >
                        <option value="free">Free</option>
                        <option value="plus">Plus</option>
                        <option value="pro">Pro</option>
                        <option value="trial">Trial</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(p.id)}
                          disabled={actioning === p.id}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs rounded-lg px-3 py-1.5"
                        >
                          Godkend
                        </button>
                        <button
                          onClick={() => handleReject(p.id)}
                          disabled={actioning === p.id}
                          className="bg-white border border-red-300 text-[var(--color-error)] hover:bg-[var(--color-error-100)] disabled:opacity-50 text-xs rounded-lg px-3 py-1.5"
                        >
                          Afvis
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-5 gap-6">
          {/* Kundeliste */}
          <div className="col-span-2 space-y-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Kunder ({customers.length})</h2>

            {customers.map(c => (
              <button
                key={c.id}
                onClick={() => { setSelected(c); setEditing(null); }}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${selected?.id === c.id ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft-bg)]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <div className="font-medium text-sm text-gray-900">{c.company_name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${planColor[c.plan] ?? planColor.free}`}>{planLabel[c.plan] ?? c.plan}</span>
                  <span className="text-xs text-gray-400">#{c.id}</span>
                </div>
              </button>
            ))}

            {/* Opret kunde */}
            <div className="border border-dashed border-gray-300 rounded-xl p-4 space-y-2">
              <p className="text-xs font-medium text-gray-600">Opret ny kunde</p>
              <input value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="Firmanavn" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
              <select value={newPlan} onChange={e => setNewPlan(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                <option value="free">Free</option>
                <option value="plus">Plus</option>
                <option value="pro">Pro</option>
                <option value="trial">Trial</option>
              </select>
              <button onClick={handleCreateCustomer} disabled={creatingCustomer || !newCompany.trim()} className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white text-sm rounded-lg px-3 py-1.5">
                {creatingCustomer ? 'Opretter…' : 'Opret'}
              </button>
            </div>
          </div>

          {/* Detaljer for valgt kunde */}
          <div className="col-span-3">
            {!selected ? (
              <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Vælg en kunde til venstre</div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold text-gray-900">{selected.company_name}</h2>
                      <p className="text-sm text-gray-500">customer_id = {selected.id}</p>
                    </div>
                    <button onClick={() => handleImpersonate(selected.id)} className="bg-[var(--color-warning-100)]0 hover:bg-amber-600 text-white text-sm rounded-lg px-4 py-2">
                      Log ind som kunde
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                    <label className="text-sm text-gray-600 font-medium">Plan</label>
                    <select
                      value={selected.plan}
                      onChange={e => handleChangePlan(e.target.value)}
                      disabled={savingPlan}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm disabled:opacity-50"
                    >
                      <option value="free">Free</option>
                      <option value="plus">Plus</option>
                      <option value="pro">Pro</option>
                      <option value="trial">Trial</option>
                    </select>
                    {savingPlan && <span className="text-xs text-gray-400">Gemmer…</span>}
                  </div>
                </div>

                {/* Login-brugere */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700">Login-brugere</h3>

                  {users.length === 0 ? (
                    <p className="text-sm text-gray-400">Ingen brugere endnu</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {users.map(u => (
                        <div key={u.id}>
                          {editing?.id === u.id ? (
                            /* Redigér-panel */
                            <div className="py-3 space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  value={editing.name}
                                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                                  placeholder="Navn"
                                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                                />
                                <input
                                  value={editing.email}
                                  onChange={e => setEditing({ ...editing, email: e.target.value })}
                                  placeholder="Email"
                                  type="email"
                                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                                />
                                <input
                                  value={editing.password}
                                  onChange={e => setEditing({ ...editing, password: e.target.value })}
                                  placeholder="Nyt password (lad stå tomt for at beholde)"
                                  type="password"
                                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm col-span-2"
                                />
                              </div>
                              {editError && <p className="text-xs text-[var(--color-error)]">{editError}</p>}
                              <div className="flex gap-2">
                                <button onClick={handleSaveEdit} disabled={saving} className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white text-sm rounded-lg px-3 py-1.5">
                                  {saving ? 'Gemmer…' : 'Gem'}
                                </button>
                                <button onClick={() => { setEditing(null); setEditError(''); }} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-1.5">
                                  Annuller
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Normal række */
                            <div className="py-2 flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium text-gray-800">{u.name}</span>
                                <span className="text-sm text-gray-400 ml-2">{u.email}</span>
                                <span className="text-xs text-gray-400 ml-2">{u.role}</span>
                              </div>
                              <button
                                onClick={() => { setEditing({ id: u.id, name: u.name, email: u.email, password: '' }); setEditError(''); }}
                                className="text-xs text-[var(--color-primary)] hover:underline"
                              >
                                Rediger
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Opret login-bruger */}
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <p className="text-xs font-medium text-gray-600">Opret login</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Navn" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                      <input value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="Email" type="email" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                      <input value={newUserPass} onChange={e => setNewUserPass(e.target.value)} placeholder="Adgangskode" type="password" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm col-span-2" />
                    </div>
                    {userError && <p className="text-xs text-[var(--color-error)]">{userError}</p>}
                    <button onClick={handleCreateUser} disabled={creatingUser || !newUserName || !newUserEmail || !newUserPass} className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white text-sm rounded-lg px-4 py-1.5">
                      {creatingUser ? 'Opretter…' : 'Opret bruger'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Sikkerhedsgrænser */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-5 max-w-lg">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">AI sikkerhedsgrænser</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Maks. dagligt AI-budget i USD (alle kunder samlet)
              </label>
              <input
                type="number" min="0.01" step="0.01"
                value={aiCaps.ai_daily_budget_usd}
                onChange={e => setAiCaps(c => ({ ...c, ai_daily_budget_usd: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32"
              />
              <span className="ml-2 text-xs text-gray-400">standard: 5.00 USD</span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Maks. Haiku-scanninger pr. time pr. kunde (beskytter mod loops)
              </label>
              <input
                type="number" min="1"
                value={aiCaps.ai_hourly_cap_per_customer}
                onChange={e => setAiCaps(c => ({ ...c, ai_hourly_cap_per_customer: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32"
              />
              <span className="ml-2 text-xs text-gray-400">standard: 10</span>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSaveAiCaps}
                disabled={savingAi}
                className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white text-sm rounded-lg px-4 py-1.5"
              >
                {savingAi ? 'Gemmer…' : 'Gem'}
              </button>
              {aiSaved && <span className="text-xs text-[var(--color-success)]">Gemt ✓</span>}
            </div>
          </div>
        </div>

        {/* AI Forbrugsoversigt */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">AI forbrug</h2>
            <div className="flex gap-1 text-xs">
              <button onClick={() => setUsageTab('own')}
                className={`px-3 py-1 rounded-lg ${usageTab === 'own' ? 'bg-[var(--color-primary)] text-white' : 'text-gray-500 hover:bg-cream-100'}`}>
                Vores log
              </button>
              <button onClick={() => { setUsageTab('anthropic'); if (!anthropicData) loadAnthropicData(); }}
                className={`px-3 py-1 rounded-lg ${usageTab === 'anthropic' ? 'bg-[var(--color-primary)] text-white' : 'text-gray-500 hover:bg-cream-100'}`}>
                Anthropic API
              </button>
            </div>
          </div>

          {usageTab === 'own' && (
            <div className="space-y-5">
              {/* Dagens budget */}
              {aiStats && (
                <div className="flex items-center gap-4">
                  <div className="text-xs text-gray-500">I dag</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="bg-[var(--color-primary)] h-2 rounded-full"
                      style={{ width: `${Math.min(100, (aiStats.today.cost_usd / aiStats.today.budget_usd) * 100)}%` }} />
                  </div>
                  <div className="text-xs font-mono text-gray-700">
                    ${aiStats.today.cost_usd.toFixed(4)} / ${aiStats.today.budget_usd.toFixed(2)}
                  </div>
                </div>
              )}

              {/* Pr. kunde denne måned */}
              {aiStats?.by_customer && aiStats.by_customer.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Denne måned — pr. kunde</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-1">Kunde</th>
                        <th className="text-right pb-1">Kald</th>
                        <th className="text-right pb-1">USD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {aiStats.by_customer.map(r => (
                        <tr key={r.customer_id}>
                          <td className="py-1 text-gray-700">{r.company_name}</td>
                          <td className="py-1 text-right text-gray-500">{r.calls}</td>
                          <td className="py-1 text-right font-mono text-gray-700">${Number(r.cost_usd).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Daglig historik */}
              {aiStats?.daily && aiStats.daily.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Daglig historik (30 dage)</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-1">Dato</th>
                        <th className="text-right pb-1">Kald</th>
                        <th className="text-right pb-1">Tokens</th>
                        <th className="text-right pb-1">USD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {aiStats.daily.map(r => (
                        <tr key={r.day}>
                          <td className="py-1 text-gray-700">{r.day}</td>
                          <td className="py-1 text-right text-gray-500">{r.calls}</td>
                          <td className="py-1 text-right text-gray-500">{Number(r.tokens).toLocaleString()}</td>
                          <td className="py-1 text-right font-mono text-gray-700">${Number(r.cost_usd).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!aiStats && <p className="text-xs text-gray-400">Ingen data endnu</p>}
            </div>
          )}

          {usageTab === 'anthropic' && (
            <div>
              {loadingStats && <p className="text-xs text-gray-400">Henter fra Anthropic…</p>}
              {anthropicError && (
                <div className="text-xs text-[var(--color-error)] space-y-1">
                  <p>{anthropicError}</p>
                  {anthropicError.includes('ANTHROPIC_ADMIN_KEY') && (
                    <p className="text-gray-500">Tilføj <code className="bg-gray-100 px-1 rounded">ANTHROPIC_ADMIN_KEY=sk-ant-admin...</code> til backend/.env</p>
                  )}
                </div>
              )}
              {anthropicData && !loadingStats && (
                <div className="space-y-4">
                  {/* Cost report */}
                  {anthropicData.cost?.data?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Daglige omkostninger (Anthropic)</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400 border-b border-gray-100">
                            <th className="text-left pb-1">Dato</th>
                            <th className="text-right pb-1">USD</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {anthropicData.cost.data.map((r: any, i: number) => (
                            <tr key={i}>
                              <td className="py-1 text-gray-700">{r.start_time?.slice(0,10)}</td>
                              <td className="py-1 text-right font-mono text-gray-700">
                                ${(Number(r.total_cost ?? 0) / 100).toFixed(4)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Raw data fallback */}
                  {!anthropicData.cost?.data?.length && (
                    <pre className="text-xs text-gray-500 bg-gray-50 p-3 rounded overflow-auto max-h-64">
                      {JSON.stringify(anthropicData, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
